/* eslint-disable jsx-control-statements/jsx-use-if-tag */
// dependencies
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

//hooks
import { useRetractSidebar } from '_hooks'

// css
import './styles.scss'

// components
import { Card, CARD_THEME, Icon, BUTTON_THEME } from '@st-operations/uikit/src/v2/index.js'
import MoreInfoDropdown from '../../components/more-info-dropdown/index.jsx'
import { getBrandedAudience } from '../../../services/partner-branded-audiences'
import Header from '_components/header/index.jsx'

const BASIC_INFORMATION = [
  { name: 'Buyable', field: 'buyable' },
  { name: 'Global ID', field: 'globalId' },
  { name: 'US ID', field: 'usId' },
]
const TTD_SPECIFIC_INFORMATION = [
  { name: '​​TTD Name', field: 'ttdName' },
  { name: '​​TTD Parent Global ID', field: 'ttdParentGlobalId' },
  { name: '​​TTD Parent US ID', field: 'ttdParentUsId' },
  { name: '​​Sync TTD Global', field: 'syncTtdGlobal' },
  { name: 'Sync TTD Global Status', field: 'syncTtdGlobalStatus' },
  { name: 'TTD Global ID', field: 'ttd-globalId' },
  { name: '​​Sync TTD US', field: 'syncTtdUs' },
  { name: 'Sync TTD US Status', field: 'syncTtdUsStatus' },
  { name: 'TTD US ID', field: 'ttd-usId' },
  { name: '​​Inherit rates', field: 'ttdShouldInheritRates' },
  { name: 'TDD Rate Type', field: 'ttdRateType', displayIfNot: 'ttdShouldInheritRates' },
  { name: '​​​​TTD CPM Rate', field: 'cpmRate', displayIfNot: 'ttdShouldInheritRates' },
  {
    name: '​​​​TTD Percent of Media Cost Rate',
    field: 'ttdPercentOfMediaCostRate',
    displayIfNot: 'ttdShouldInheritRates',
  },
]
const APP_NEXUS_SPECIFIC_INFORMATION = [
  { name: '​​AppNexus Global Name', field: 'appnexusGlobalName' },
  { name: '​​AppNexus Global ID', field: 'appnexusGlobalId' },
  { name: '​​Sync AppNexus Global', field: 'syncAppnexusGlobal' },
  { name: '​​Sync AppNexus Global Status', field: 'syncAppnexusGlobalStatus' },
  { name: '​​AppNexus CPM Rate', field: 'appnexusCpmRate' },
  { name: '​​AppNexus US Name', field: 'appnexusUsName' },
  { name: '​​AppNexus US ID', field: 'appnexusUsId' },
  { name: '​​Sync AppNexus US', field: 'syncAppnexusUs' },
  { name: '​​Sync AppNexus US Status', field: 'syncAppnexusUsStatus' },
]

const shouldDisplayItem = (data, item) => {
  switch (item.field) {
    case 'ttd-globalId':
      return data['syncTtdGlobal']
    case 'ttd-usId':
      return data['syncTtdUs']
    case 'appnexusGlobalId':
      return data['syncAppnexusGlobal']
    case 'appnexusUsId':
      return data['syncAppnexusUs']
    default:
      return (!item.displayIfNot || data[item.displayIfNot]) && data[item.field]
  }
}

const displayField = (data, field) => {
  switch (field) {
    case 'ttd-globalId':
      return <a href={`/ttd-audiences/${data['globalId']}`}>{data['globalId']}</a>
    case 'ttd-usId':
      return <a href={`/ttd-audiences/${data['usId']}`}>{data['usId']}</a>
    case 'appnexusGlobalId':
      return (
        <a href={`/appnexus-audiences/${data['appnexusGlobalId']}`}>{data['appnexusGlobalId']}</a>
      )
    case 'appnexusUsId':
      return <a href={`/appnexus-audiences/${data['appnexusUsId']}`}>{data['appnexusUsId']}</a>
    default:
      return typeof data[field] === 'boolean' ? (data[field] ? 'Yes' : 'No') : data[field]
  }
}

const IndividualRecords = ({
  className,
  match: {
    params: { id },
  },
}) => {
  const [showInfo, setShowInfo] = useState(false)
  const [data, setData] = useState({})

  useRetractSidebar()

  useEffect(() => {
    getBrandedAudience(id).then((pba) => {
      setData(pba)
    })
  }, [])

  const handleShowInfo = () => {
    setShowInfo((prevstate) => !prevstate)
  }

  const handleExit = (event) => {
    event.preventDefault()
    window.history.go(-1)
  }

  return (
    <div className={classnames(className, 'individual-records')}>
      <Header
        title="Individual Records"
        actions={[
          {
            children: 'Exit',
            onClick: handleExit,
          },
          {
            theme: BUTTON_THEME.GHOST,
            children: (
              <Icon
                type="outline"
                icon="info"
                color="#13682B"
                className="header__button"
                width="24px"
                height="24px"
              />
            ),
            onClick: handleShowInfo,
          },
        ]}
      />
      <If condition={showInfo}>
        <MoreInfoDropdown onClose={handleShowInfo} />
      </If>
      <h1 className="individual-records__title">{data.name}</h1>
      <Card theme={CARD_THEME.NO_SHADOW} className="individual-records__card">
        <h1 className="individual-records__card--title">Basic Information</h1>
        <div className="individual-records__card--basic-container">
          <For each="item" of={BASIC_INFORMATION}>
            <If condition={shouldDisplayItem(data, item)}>
              <div className="individual-records__card--content">
                <h2 className="individual-records__card--subtitle">{item.name}</h2>
                <p className="individual-records__card--description">
                  {displayField(data, item.field)}
                </p>
              </div>
            </If>
          </For>
        </div>
      </Card>
      <Card theme={CARD_THEME.NO_SHADOW} className="individual-records__card">
        <h1 className="individual-records__card--title">TTD Specific Information</h1>
        <div className="individual-records__card--specific-container">
          <For each="item" of={TTD_SPECIFIC_INFORMATION}>
            <If condition={shouldDisplayItem(data, item)}>
              <div className="individual-records__card--content">
                <h2 className="individual-records__card--subtitle">{item.name}</h2>
                <p className="individual-records__card--description">
                  {displayField(data, item.field)}
                </p>
              </div>
            </If>
          </For>
        </div>
      </Card>
      <Card theme={CARD_THEME.NO_SHADOW} className="individual-records__card">
        <h1 className="individual-records__card--title">AppNexus Specific Information</h1>
        <div className="individual-records__card--individual-container">
          <For each="item" of={APP_NEXUS_SPECIFIC_INFORMATION}>
            <If condition={shouldDisplayItem(data, item)}>
              <div className="individual-records__card--content">
                <h2 className="individual-records__card--subtitle">{item.name}</h2>
                <p className="individual-records__card--description">
                  {displayField(data, item.field)}
                </p>
              </div>
            </If>
          </For>
        </div>
      </Card>
    </div>
  )
}

IndividualRecords.propTypes = {
  className: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string }),
  }),
}

IndividualRecords.defaultProps = {
  className: '',
  match: {},
}

export default IndividualRecords
